import { type ReactNode } from "react";
import { Info } from "lucide-react";
import { Card } from "@sfc/ui";
import { cn } from "@client/utils/cn";

export function ApplicationSubmissionStatus({
  submitted,
  hasUnsubmittedChanges = false,
}: {
  submitted: boolean;
  hasUnsubmittedChanges?: boolean;
}) {
  const needsSubmission = !submitted || hasUnsubmittedChanges;
  return (
    <span
      className={cn(
        "body-sm font-medium",
        needsSubmission ? "text-text-secondary" : "text-success",
      )}
    >
      {!submitted
        ? "Not submitted"
        : hasUnsubmittedChanges
          ? "Changes not submitted"
          : "Submitted"}
    </span>
  );
}

export function ApplicationSubmissionNotice({
  submitted,
  hasUnsubmittedChanges = false,
  readOnly = false,
  action,
}: {
  submitted: boolean;
  hasUnsubmittedChanges?: boolean;
  readOnly?: boolean;
  action?: ReactNode;
}) {
  const needsSubmission = !submitted || hasUnsubmittedChanges;
  return (
    <Card
      className={cn(
        "gap-4 p-4 sm:p-5",
        needsSubmission &&
          !readOnly &&
          "border-accent/30 border-l-4 border-l-accent/50 bg-accent/5",
      )}
    >
      <div className="flex items-start gap-3">
        <Info
          aria-hidden="true"
          className="mt-0.5 size-5 shrink-0 text-accent"
        />
        <div className="min-w-0 space-y-2">
          <p className="heading-sm">
            {!submitted
              ? readOnly
                ? "Your application has not been submitted"
                : "Continue your application"
              : hasUnsubmittedChanges
                ? "Your latest changes have not been submitted"
                : "Your application is submitted"}
          </p>
          <p className="body-md text-text-secondary">
            {!submitted
              ? readOnly
                ? "You can read your saved answers, but editing and submission are currently unavailable."
                : "Your answers are saved, but your application has not been submitted. Pick up where you left off."
              : hasUnsubmittedChanges
                ? readOnly
                  ? "Reviewers still see your previous submission. Editing and submission are currently unavailable."
                  : "Reviewers see the submitted answers below. Continue editing, then select Submit changes to send your updates."
                : readOnly
                  ? "These are the submitted answers reviewers can see. Editing and submission are currently unavailable."
                  : "Reviewers can see your submitted answers. Any further edits must be submitted using Submit changes."}
          </p>
        </div>
      </div>
      {!readOnly && action ? (
        <div className="flex flex-col items-stretch sm:items-start">
          {action}
        </div>
      ) : null}
    </Card>
  );
}
